<template>
  <div class="audit-export flex flex-col w-full h-full p-4 gap-4">
    <!-- Header -->
    <div class="flex justify-between items-center">
      <h1 class="text-2xl font-bold">דוחות וביקורת מס</h1>
    </div>

    <!-- Explanation -->
    <div class="bg-white p-4 rounded-lg shadow-sm flex gap-3 items-start">
      <i class="pi pi-shield text-primary text-2xl mt-1"></i>
      <div class="text-sm text-gray-600 leading-relaxed">
        <p class="font-semibold text-gray-800 mb-1">ייצוא נתונים לביקורת</p>
        <p>
          הפקת קבצי אקסל (XLSX) להצגה בפני מפקח מס. בחרו טווח תאריכים (אופציונלי)
          ולחצו על הכפתור הרצוי.
        </p>
      </div>
    </div>

    <!-- Date Range + Exports -->
    <div class="flex flex-wrap gap-4 items-end bg-white p-4 rounded-lg shadow-sm">
      <div class="flex flex-col gap-1">
        <label class="text-sm text-gray-500">טווח תאריכים</label>
        <Calendar
          v-model="dateRange"
          selectionMode="range"
          :manualInput="false"
          dateFormat="dd/mm/yy"
          placeholder="כל הזמן (ללא סינון)"
          showButtonBar
          class="w-72"
        />
      </div>

      <Button
        :loading="isExportingAppointments"
        icon="pi pi-calendar"
        label="ייצוא יומן תורים"
        severity="secondary"
        @click="exportAppointments"
      />

      <Button
        :loading="isExportingAudit"
        icon="pi pi-file-excel"
        label="ייצוא יומן שינויים (Audit)"
        @click="exportAuditLog"
      />
    </div>

    <!-- Hints -->
    <div class="text-xs text-gray-400 flex flex-col gap-1 px-1">
      <span>• "יומן תורים" — רשימת כל התורים בטווח (מצב נוכחי).</span>
      <span>• "יומן שינויים" — תיעוד מלא של יצירה, עדכון, ביטול ומחיקה של תורים.</span>
    </div>
  </div>
</template>

<script setup lang="ts">
import { onMounted, ref } from 'vue';
import { DateTime } from 'luxon';
import Calendar from 'primevue/calendar';
import Button from 'primevue/button';
import { useToast } from 'vue-toastification';
import { ReportsApi } from '@app/api/admin';
import { useAdminStore } from '@app/stores';
import { AdminRouteNames } from '@app/router/route-names';

const toast = useToast();
const adminStore = useAdminStore();

onMounted(() => {
  adminStore.setPage(AdminRouteNames.Reports);
});

const isExportingAudit = ref(false);
const isExportingAppointments = ref(false);
const dateRange = ref<Date[]>([]);

const rangeAsStrings = (): [string | undefined, string | undefined] => {
  const [from, to] = dateRange.value ?? [];
  return [
    from ? DateTime.fromJSDate(from).toFormat('yyyy-MM-dd') : undefined,
    to ? DateTime.fromJSDate(to).toFormat('yyyy-MM-dd') : undefined,
  ];
};

const downloadBlob = (blob: Blob, fileName: string) => {
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = fileName;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  URL.revokeObjectURL(url);
};

const exportAuditLog = async () => {
  isExportingAudit.value = true;
  try {
    const [fromStr, toStr] = rangeAsStrings();
    const blob = await ReportsApi.exportAuditLog(fromStr, toStr);
    downloadBlob(blob, `audit-log-${DateTime.now().toFormat('yyyy-MM-dd')}.xlsx`);
  } catch (e) {
    toast.error('ייצוא יומן השינויים נכשל, נסו שוב');
  } finally {
    isExportingAudit.value = false;
  }
};

const exportAppointments = async () => {
  isExportingAppointments.value = true;
  try {
    const [fromStr, toStr] = rangeAsStrings();
    const blob = await ReportsApi.exportAppointments(fromStr, toStr);
    downloadBlob(blob, `appointments-${DateTime.now().toFormat('yyyy-MM-dd')}.xlsx`);
  } catch (e) {
    toast.error('ייצוא יומן התורים נכשל, נסו שוב');
  } finally {
    isExportingAppointments.value = false;
  }
};
</script>
